// src/pages/StudentDashboard.jsx
import React, { useState } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../firebase';
import InternalMessenger from '../components/InternalMessenger'; 
import Scheduler from '../components/Scheduler';
import CapstoneProject from '../components/CapstoneProject';
import LifeMission from '../components/LifeMission';
import { sendEmailVerification, updateProfile } from 'firebase/auth';
import DiagnosticTest from '../components/DiagnosticTest';

const StudentDashboard = () => {
  const [user] = useAuthState(auth);
  const [activeTab, setActiveTab] = useState('Level');
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const tabs = [
    { id: 'Level', label: '📊 CEFR Level' },
    { id: 'Schedule', label: '📅 Schedule' },
    { id: 'Pathway', label: '🚀 Mastery Pathway' },
    { id: 'Materials', label: '📚 Materials' },
    { id: 'Practice', label: '🎭 Practice' },
    { id: 'Capstone', label: '🎓 Capstone Project' },
    { id: 'Mission', label: '🌟 Life Mission' },
  ];

  const handleUpdateName = async () => {
    const firstName = prompt("First Name:");
    const lastName = prompt("Last Name:");
    if (firstName) {
        await updateProfile(user, { displayName: `${firstName} ${lastName}` });
        window.location.reload();
    }
  };

  const handleResendVerification = async () => {
    if(user) await sendEmailVerification(user);
    alert("Email sent!");
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col md:flex-row relative">
      
      {/* MOBILE HEADER */}
      <div className="md:hidden bg-white p-4 shadow flex justify-between items-center z-20 relative">
        <h1 className="text-xl font-bold text-indigo-700">Aulice Student</h1>
        <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-2xl text-gray-700 focus:outline-none">
          {isMobileMenuOpen ? '✖' : '☰'}
        </button>
      </div>

      {/* MOBILE OVERLAY */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        ></div>
      )}

      {/* SIDEBAR (Slide-in) */}
      <div className={`
        fixed inset-y-0 left-0 transform ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} 
        md:relative md:translate-x-0 transition-transform duration-300 ease-in-out
        w-64 bg-white shadow-lg z-40 flex flex-col h-full
      `}>
        <div className="p-6 border-b">
          <h1 className="text-2xl font-bold text-indigo-700 hidden md:block">Aulice Student</h1>
          <p className="font-semibold text-gray-800 mt-4">Welcome, {user?.displayName?.split(' ')[0] || 'Scholar'}</p>
          <p className="text-xs text-gray-500 truncate">{user?.email}</p>
          {!user?.displayName && <button onClick={handleUpdateName} className="text-xs text-indigo-600 underline mt-1 block">+ Add Name</button>}
          {!user?.emailVerified && <button onClick={handleResendVerification} className="mt-2 w-full bg-yellow-100 text-yellow-800 text-xs py-1 rounded">Resend Email</button>}
        </div>
        <nav className="flex-1 overflow-y-auto mt-2">
          {tabs.map(tab => (
            <button 
              key={tab.id} 
              onClick={() => { setActiveTab(tab.id); setIsMobileMenuOpen(false); }} 
              className={`w-full text-left px-6 py-4 hover:bg-gray-50 transition-colors ${activeTab === tab.id ? 'border-l-4 border-indigo-600 bg-indigo-50 font-semibold text-indigo-700' : 'text-gray-600'}`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* MAIN CONTENT */}
      <div className="flex-1 p-4 md:p-8 h-screen overflow-y-auto">
        <header className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800">{tabs.find(t => t.id === activeTab)?.label}</h2>
          </div>
          <button onClick={() => setIsChatOpen(!isChatOpen)} className="bg-indigo-600 text-white px-4 py-2 rounded-full shadow w-full md:w-auto hover:bg-indigo-700 transition">
            ⚡ The Synapse
          </button>
        </header>

        <div className="bg-white rounded-xl shadow p-4 md:p-6 min-h-[500px]">
          {activeTab === 'Schedule' && <Scheduler user={user} role="student" />}
          {activeTab === 'Capstone' && <CapstoneProject user={user} />}
          {activeTab === 'Mission' && <LifeMission user={user} />}
          
          {['Level', 'Pathway', 'Materials', 'Practice'].includes(activeTab) && (
             <div className="text-center p-10 text-gray-500">
               <h3 className="text-xl font-bold mb-2">{tabs.find(t=>t.id===activeTab).label}</h3>
               {/* 1. CEFR Level Tab */}
{activeTab === 'Level' && (
  <div className="w-full">
    {/* If user already has a level, show it. If not, show test. */}
    {user?.cefrLevel ? (
      <div className="text-center p-10">
        <h3 className="text-2xl font-bold text-gray-800 mb-2">Your Current Level</h3>
        <div className="inline-block bg-green-100 text-green-800 text-5xl font-extrabold px-8 py-4 rounded-2xl my-6 shadow-sm border border-green-200">
          {user.cefrLevel}
        </div>
        <p className="text-gray-600 max-w-md mx-auto">
          You are placed at {user.cefrLevel}. This determines the difficulty of your Scenarios and Argot drills.
        </p>
        <button 
          onClick={() => { /* Logic to reset level if needed */ }}
          className="mt-8 text-sm text-gray-400 underline hover:text-gray-600"
        >
          Retake Diagnostic Test
        </button>
      </div>
    ) : (
      <div>
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-gray-800">Diagnostic Assessment</h2>
          <p className="text-gray-500">Take this quick test to calibrate your dashboard.</p>
        </div>
        <DiagnosticTest user={user} />
      </div>
    )}
  </div>
)}
             </div>
          )}
        </div>
      </div>
      <InternalMessenger isOpen={isChatOpen} onClose={() => setIsChatOpen(false)} />
    </div>
  );
};

export default StudentDashboard;